import { locale } from 'devextreme/localization'

import apiEndpoints from '../api/api-endpoints'
import { SetCookies, Token } from './default-cookies'
import { getReactParameters } from './iframe-func'

const getAuthEndpoint = `${apiEndpoints.baseUrl}api/Authorization/GetAuth`
const authRequests = new Map()

function isEmptyPayload(data) {
  return !data || data === 'empty' || typeof data !== 'object'
}

function getPayloadValue(payload, key) {
  return payload?.[key] ?? payload?.[key.charAt(0).toUpperCase() + key.slice(1)]
}

function normalizeOrigin(configuredUrl) {
  try {
    let normalizedUrl = (configuredUrl || '').trim()
    if (!normalizedUrl) return null

    if (normalizedUrl.startsWith('//')) {
      normalizedUrl = `${window.location.protocol}${normalizedUrl}`
    } else if (
      /^(localhost|127\.0\.0\.1|\[::1\])(?::|\/|$)/i.test(normalizedUrl)
    ) {
      normalizedUrl = `http://${normalizedUrl}`
    } else if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(normalizedUrl)) {
      normalizedUrl = `${window.location.protocol}//${normalizedUrl}`
    }

    const origin = new URL(normalizedUrl).origin
    return origin && origin !== 'null' ? origin : null
  } catch (e) {
    return null
  }
}

function getAlayaOrigin() {
  const parentOrigin = normalizeOrigin(document.referrer)

  // Allow local Alaya parent only when explicitly enabled
  if (
    shouldAllowLocalhostParent() &&
    parentOrigin &&
    isLocalhostOrigin(parentOrigin)
  ) {
    return parentOrigin
  }

  const configuredOrigin = normalizeOrigin(apiEndpoints.apiAlayaUrl)

  if (!parentOrigin) {
    return configuredOrigin
  }

  if (
    parentOrigin === configuredOrigin ||
    isSameHttpHost(parentOrigin, configuredOrigin)
  ) {
    return parentOrigin
  }

  const allowedOrigins = (process.env.REACT_APP_ALAYA_ALLOWED_ORIGINS || '')
    .split(',')
    .map(normalizeOrigin)
    .filter(Boolean)

  const isAllowedOrigin = allowedOrigins.some(
    (allowedOrigin) =>
      parentOrigin === allowedOrigin ||
      isSameHttpHost(parentOrigin, allowedOrigin),
  )

  if (isAllowedOrigin) {
    return parentOrigin
  }

  return configuredOrigin
}

async function redeemTicketRequest(ticket) {
  const searchParams = new URLSearchParams({ ticket })

  const response = await fetch(`${getAuthEndpoint}?${searchParams}`, {
    method: 'GET',
    headers: new Headers({
      accept: 'application/json',
      'Content-Type': 'application/json',
      'X-App-Context': process.env.REACT_APP_BASE_PATH || '/',
    }),
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`)
  }

  const result = await response.json()
  if (!result?.Result) {
    throw new Error(result?.Message || 'Authentication failed')
  }

  return result.Obj || {}
}

function redeemTicket(ticket) {
  const existingRequest = authRequests.get(ticket)
  if (existingRequest) return existingRequest

  const request = redeemTicketRequest(ticket).finally(() => {
    authRequests.delete(ticket)
  })
  authRequests.set(ticket, request)
  return request
}

function notifyEmbeddedAuthError(payload) {
  window.dispatchEvent(
    new CustomEvent('embedded-auth-error', {
      detail: payload,
    }),
  )
}

function applyEmbeddedAuthPayload(cookies, payload) {
  const accessToken = getPayloadValue(payload, 'accessToken')
  if (!accessToken) return false

  SetCookies(
    cookies,
    accessToken,
    getPayloadValue(payload, 'userID'),
    getPayloadValue(payload, 'companyID'),
    getPayloadValue(payload, 'clientID'),
    getPayloadValue(payload, 'username'),
    getPayloadValue(payload, 'adminYN'),
    getPayloadValue(payload, 'administratorYN'),
    getPayloadValue(payload, 'language'),
  )

  const language = getPayloadValue(payload, 'language') || 'en'
  sessionStorage.setItem('locale', language)
  locale(language)

  return true
}

export async function authenticateEmbeddedMessage(
  eventOrPayload,
  cookies,
  options = {},
) {
  const rawPayload = eventOrPayload?.data ?? eventOrPayload
  if (isEmptyPayload(rawPayload)) return false
  const alayaOrigin = getAlayaOrigin()
  if (
    eventOrPayload?.origin &&
    alayaOrigin !== '*' &&
    eventOrPayload.origin !== alayaOrigin
  )
    return false

  if (rawPayload.authError) {
    notifyEmbeddedAuthError(rawPayload)
    options.onAuthError?.(rawPayload)
    return false
  }

  if (options.skipWhenTokenExists !== false && Token()) return false

  const ticket = getPayloadValue(rawPayload, 'ticket')
  if (!ticket) return false

  let authResult
  try {
    authResult = await redeemTicket(ticket)
  } catch (error) {
    const authError = {
      targetApp: 'ALAYA_REACT',
      authError: true,
      errorCode: 'ALAYA_REACT_AUTH_FAILED',
      message: 'Please login again.',
    }
    notifyEmbeddedAuthError(authError)
    options.onAuthError?.(authError)
    return false
  }

  const payload = {
    ...rawPayload,
    accessToken: authResult.AccessToken || authResult.accessToken,
  }

  const authenticated = applyEmbeddedAuthPayload(cookies, payload)
  if (authenticated) {
    options.onAuthenticated?.(payload)
  }

  return authenticated
}

export function requestEmbeddedAuth() {
  const alayaOrigin = getAlayaOrigin()
  if (!alayaOrigin) return

  window.parent?.postMessage(
    getReactParameters(new URL(alayaOrigin).host),
    alayaOrigin,
  )
}

function shouldAllowLocalhostParent() {
  return process.env.REACT_APP_ALAYA_ALLOW_LOCALHOST_PARENT === 'true'
}

function isLocalhostOrigin(origin) {
  try {
    const { hostname } = new URL(origin)

    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' ||
      hostname === '::1'
    )
  } catch {
    return false
  }
}

function isSameHttpHost(originA, originB) {
  try {
    const urlA = new URL(originA)
    const urlB = new URL(originB)

    const isHttpProtocolA =
      urlA.protocol === 'http:' || urlA.protocol === 'https:'
    const isHttpProtocolB =
      urlB.protocol === 'http:' || urlB.protocol === 'https:'

    return (
      isHttpProtocolA &&
      isHttpProtocolB &&
      urlA.hostname === urlB.hostname &&
      urlA.port === urlB.port
    )
  } catch {
    return false
  }
}
